import React from 'react';
import { ArrowLeft, Printer } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { SavedAGB, agbProvider, generateAGBTitle, getEffectiveClauses, resolveAGBPlaceholders } from '@/lib/constants/agbTemplateData';
import { renderClauseContent } from '@/components/admin/avv/renderClauseContent';

interface AGBDocumentRendererProps {
  agb: SavedAGB;
  onBack: () => void;
}

const AGBDocumentRenderer: React.FC<AGBDocumentRendererProps> = ({ agb, onBack }) => {
  const handlePrint = () => window.print();
  const effectiveClauses = getEffectiveClauses(agb);

  return (
    <>
      <style>{`
        @media print {
          .no-print { display: none !important; }
          .print-doc { 
            padding: 0 !important; 
            max-width: 100% !important;
            font-size: 11pt !important;
            line-height: 1.5 !important;
            color: #000 !important;
          }
          .print-doc h1 { font-size: 18pt !important; }
          .print-doc h2 { font-size: 14pt !important; page-break-after: avoid; }
          .print-doc .signature-block { page-break-inside: avoid; }
          body { background: white !important; }
        }
      `}</style>

      <div className="no-print flex items-center justify-between mb-6">
        <Button variant="ghost" size="sm" onClick={onBack}>
          <ArrowLeft className="h-4 w-4 mr-1" />
          Zurück zur Vorschau
        </Button>
        <Button onClick={handlePrint}>
          <Printer className="h-4 w-4 mr-2" />
          Drucken / Als PDF speichern
        </Button>
      </div>

      <div className="print-doc max-w-4xl mx-auto bg-background p-8 md:p-12 rounded-lg border space-y-6">
        <div className="text-center space-y-4 pb-8">
          <h1 className="text-2xl font-bold">Allgemeine Geschäftsbedingungen</h1>
          <p className="text-lg">für SaaS-Dienste der PaperVerse-Plattform</p>
          <p className="text-muted-foreground">{generateAGBTitle(agb.customer.name)}</p>
        </div>

        <hr />

        <div className="grid grid-cols-2 gap-8 text-sm">
          <div>
            <p className="font-bold mb-1">Kunde (Auftraggeber):</p>
            <p>{agb.customer.name || '[Kundenname]'}</p>
            <p>{agb.customer.address || '[Adresse]'}</p>
            {agb.customer.representedBy && <p>Vertreten durch: {agb.customer.representedBy}</p>}
          </div>
          <div>
            <p className="font-bold mb-1">Anbieter (Auftragnehmer):</p>
            <p>{agbProvider.name}</p>
            <p>{agbProvider.address}</p>
            <p>Vertreten durch: {agbProvider.representedBy}</p>
          </div>
        </div>

        <hr />

        {effectiveClauses.map(clause => {
          const content = resolveAGBPlaceholders(clause.content, agb);
          return (
            <div key={clause.id} className="space-y-2">
              <h2 className="text-base font-bold mt-4">{clause.title}</h2>
              <div className="text-sm">{renderClauseContent(content)}</div>
            </div>
          );
        })}

        <div className="signature-block grid grid-cols-2 gap-12 pt-12 mt-8">
          <div>
            <p className="text-sm mb-8">Ort, Datum</p>
            <div className="border-b border-black w-full mb-2" />
            <p className="text-sm">{agb.customer.name || '[Kundenname]'}</p>
            <p className="text-xs">(Kunde)</p>
          </div>
          <div>
            <p className="text-sm mb-8">Ort, Datum</p>
            <div className="border-b border-black w-full mb-2" />
            <p className="text-sm">{agbProvider.name}</p>
            <p className="text-xs">(Anbieter)</p>
          </div>
        </div>
      </div>
    </>
  );
};

export default AGBDocumentRenderer;
